<template>
  <v-container class="mb-4 pa-5">
    <h1 class="display-1 font-weight-bold">
      What are Insurance Stakes? When and how to use them?
    </h1>

    <h2 class="mt-6">What are "Insurance Stakes"?</h2>
    <p>
      Insurance Stakes are a second layer <a
        class="textlink"
        href="https://etherscan.io/address/0x7924a6d4d5994cae4c82481eee253e342437e656"
        target="_blank"
      >contract</a> on top of the <a
        class="textlink"
        href="https://etherscan.io/address/0x66a0f676479Cee1d7373f3DC2e2952778BfF5bd6"
        target="_blank"
      >original contract</a> of <ReferrerLink>WISE</ReferrerLink>, created by the Wise Foundation.<br />
      The purpose of an "Insurance Stake" is to act as a kind of an insurance against early unstaking penalties. If you unstake a regular stake too early (before it matured), you have to pay a penalty of 10%-90% on your principle which can be quite
      heavy.
      If you create an "Insurance stake" instead of a regular stake, you get back 90% of your principle if you unstake early. If you let the stake mature, you will get back 100% of your principle plus 90% of the interest (compared to a regular
      stake).
    </p>
    <h3>
      Comparison between "Insurance Stakes" and regular stakes:
    </h3>
    <v-simple-table class="mt-3">
      <thead>
        <tr>
          <th></th>
          <th>Insurance Stake</th>
          <th>Regular Stake</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Duration</td>
          <td>1 day - 3 years</td>
          <td>1 day - 42 years</td>
        </tr>
        <tr>
          <td>Return on matured stakes</td>
          <td>100% of the principle + generated interest of 90% of your stake</td>
          <td>100% of the principle + generated interest of 100% of your stake</td>
        </tr>
        <tr>
          <td>Return on pre-matured stakes<br />("early unstake")</td>
          <td>90% of the principle, no interest</td>
          <td>10%-90% of the principle + generated interest during the staked time</td>
        </tr>
        <tr>
          <td>Scraping rewards</td>
          <td>Not possible</td>
          <td>Possible</td>
        </tr>
        <tr>
          <td>Cancelling pending stakes</td>
          <td>Not possible</td>
          <td>Possible</td>
        </tr>
        <tr>
          <td>Insurance cost</td>
          <td>10% of shares (only 90% of your stake will generate interest)</td>
          <td>No costs</td>
        </tr>
      </tbody>
    </v-simple-table>

    <h2 class="mt-8">When to use Insurance Stakes</h2>
    <p>
      It depends on ones personal preferences and risk affinity if he/she wants to create insurance stakes or regular stakes. Insurance stakes have the advantage that they return a lot more of the priciple if the stake is ended early. The
      disadvantages are that you can stake only for a maximum of 3 years and you get 90% of the interest a regular stake would create in the same time.
    </p>
    <p>
      Following situations may lead to using Insurance Stakes (but don't have to...)
    </p>
    <ul>
      <li>
        You see a chance that you will have to cancel your stake before it ends and you don't want to pay a hefty penalty for this.
      </li>
      <li>
        You initially wanted to keep some WISE liquid but you still want to earn interest on it (and you accept the listed costs of insturance stakes if you unstake early)
      </li>
    </ul>

    <h2 class="mt-8">How to create insurance stakes?</h2>
    <p>
      Creating insurance stakes works almost the same as creating regular stakes. Follow this step by step guide:
    </p>
    <ol>
      <li>
        Go to
        <ReferrerLinkStaking />
      </li>
      <li>
        Connect Metamask
      </li>
      <li>
        Click "Insurance Staking"
      </li>
      <li>
        Click "Create Insurance Stake (WISE)" (or "Create Insurance Stake (ETH)" if you have no WISE yet, but ETH)
      </li>
      <v-img
        class="ma-2"
        src="../assets/howto_insurancestaking_metamask-insurancestaking.png"
        lazy-src="../assets/howto_insurancestaking_metamask-insurancestaking.png"
        width="500"
        elevation="10"
      ></v-img>
      <li>
        Read the Insurance Rules and click "Approve the Insurance Contract"
      </li>
      <v-img
        class="ma-2"
        src="../assets/howto_insurancestaking_approvecontract.png"
        lazy-src="../assets/howto_insurancestaking_approvecontract.png"
        width="500"
      ></v-img>
      <li>
        Approve it in Metamask
      </li>
      <v-img
        class="ma-2"
        src="../assets/howto_insurancestaking_metamaskconfirmation.png"
        lazy-src="../assets/howto_insurancestaking_metamaskconfirmation.png"
        width="300"
      ></v-img>
      <li>
        Create the stake like a regular stake by adding the amount, the stake duration in days, and verify that you use a Referral Address, (preferrable the one from wiseinfo.io, beginning with 0x280589...) to get an additional +10% more shares.
      </li>
      <li>
        Click "Create Insurance Stake"
      </li>
      <v-img
        class="ma-2"
        src="../assets/howto_insurancestaking_stake.png"
        lazy-src="../assets/howto_insurancestaking_stake.png"
        width="500"
      ></v-img>
      <li>
        Confirm the transaction in Metamask
        <ol type="a">
          <li>
            Verify it is sent from the right account
          </li>
          <li>
            Verify it is sent to the right contract <a
              target="_blank"
              href="https://etherscan.io/address/0x7924a6d4d5994cae4c82481eee253e342437e656"
            >0x7924a6d4d5994cae4c82481eee253e342437e656</a>
          </li>
          <li>
            Modify the gas price accordingly, check it on <a
              href="https://etherscan.io/gastracker"
              target="_blank"
            >https://etherscan.io/gastracker</a>
          </li>
          <li>
            Click "Confirm"
          </li>
        </ol>
      </li>
      <v-img
        class="ma-2"
        src="../assets/howto_insurancestaking_metamaskstake.png"
        lazy-src="../assets/howto_insurancestaking_metamaskstake.png"
        width="300"
      ></v-img>
      <li>
        Check the newly created stake
      </li>
      <v-img
        class="ma-2"
        src="../assets/howto_insurancestaking_createdstake.png"
        lazy-src="../assets/howto_insurancestaking_createdstake.png"
        width="500"
      ></v-img>
    </ol>
  </v-container>
</template>

<script>

  import ReferrerLink from "@/components/ReferrerLink.vue"
  import ReferrerLinkStaking from "@/components/ReferrerLinkStaking.vue"

  export default {
    name: "InsuranceStaking",
    data() {
      return {}
    },
    components: {
      ReferrerLink,
      ReferrerLinkStaking
    },
  };
</script>

<style>
</style>
